import React from "react";
import { useNavigate } from "react-router-dom";
import "./LegalDocument.css";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <main className="legal-page">
      <button type="button" className="legal-back-button" onClick={() => navigate(-1)}>
        Back
      </button>

      <header className="legal-header">
        <p className="legal-kicker">Privacy</p>
        <h1>BlueWire Privacy Policy</h1>
        <p className="legal-effective"><strong>Effective Date:</strong> 11/04/2026</p>
      </header>

      <section className="legal-section">
        <p>
          At BlueWire, we are committed to protecting the privacy and security of our users.
          This Privacy Policy explains what information we collect, how we use it, and the
          choices you have regarding your data.
        </p>
      </section>

      <section className="legal-section">
        <h2>Information We Collect</h2>
        <p>When you create an account and use BlueWire, we may collect:</p>
        <ul>
          <li>Name, work email address, phone number, agency, rank, and assignment</li>
          <li>Identity-verification information described below</li>
          <li>Private Notes, photographs, person records, and interaction notes you create</li>
          <li>Information you choose to share through Fieldbook</li>
          <li>Messages, BOLO alerts, search activity, and related service records</li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Identity Verification</h2>
        <p>
          Registration requires identity and law-enforcement eligibility verification. You
          submit a photograph of a department-issued credential and a selfie. Before those
          images are submitted, BlueWire requests your express consent to collect and process
          them for identity and eligibility verification.
        </p>
        <p>
          AWS Textract processes credential information to extract text. AWS Rekognition
          performs a one-time facial comparison between the selfie and the credential
          photograph. Verification images are used for identity confirmation and platform
          security, not advertising or unrelated commercial purposes.
        </p>
        <p>
          Registration images and their retention are separate from facial-search indexes
          associated with Private Notes or public booking photographs. Those indexes are
          described in the sections below and are retained and removed under their respective
          policies.
        </p>
      </section>

      <section className="legal-section">
        <h2>Public Booking Photographs and Facial Search</h2>
        <h3>Information collected and acquisition</h3>
        <p>
          BlueWire obtains booking photographs and limited accompanying information from
          publicly accessible county booking-record websites. Automated software navigates
          those public interfaces, accesses individual records, and retrieves photographs
          and selected identifying information through the same general navigation and
          retrieval actions available to a person using the public interface. Current sources
          include Wake, Mecklenburg, and Harnett counties in North Carolina.
        </p>
        <p>
          The information retained for the public booking-photo facial-search collection is
          limited to the booking photograph, the person's name, the date associated with the
          photograph or source entry, and the county and source-record reference. BlueWire
          does not retain criminal charges, convictions, case proceedings, sentencing
          information, or other criminal-history details from those source records.
        </p>
        <h3>Facial indexing and search</h3>
        <p>
          Where public booking-photo facial search is enabled, AWS Rekognition processes
          booking photographs to create facial-search indexes. Verified BlueWire users may
          submit a photograph for comparison against the public booking-photo collection.
          A search photograph is uploaded to temporary BlueWire storage, normalized for
          processing, and sent to AWS Rekognition for face detection and comparison. BlueWire
          requests deletion of the uploaded temporary object after processing, including
          when a search cannot be completed. No fixed retention period is stated for that
          temporary upload; technical failures or delays may delay cleanup.
        </p>
        <p>
          Search results are potential facial matches, not verified identifications. Source
          information may be outdated or incomplete. Users are instructed to verify identity
          independently before relying on a result. A booking photograph or facial match is
          not proof of guilt, conviction, or current criminal status. BlueWire is not a
          substitute for official criminal-justice systems or records.
        </p>
        <h3>Retention, removal, and suppression</h3>
        <p>
          Public booking records are retained for up to ten years from BlueWire's first
          observation of the record, subject to earlier removal or suppression where
          applicable. When a source record or photograph is removed or corrected, a record
          is suppressed, or removal is otherwise approved, BlueWire can disable search access
          and remove the associated photograph, facial index, and related records through
          its removal and cleanup processes. Automated cleanup processes are implemented,
          but deletion depends on the applicable process being enabled and completing
          successfully. Technical delays may extend the time before deletion is complete.
        </p>
      </section>

      <section className="legal-section">
        <h2>Private Notes</h2>
        <p>
          Private Notes let an individual user create person records and interaction notes
          for that user's own reference. A record may include photographs and associated
          facial-search data. Private facial-search collections are isolated by user. Other
          users cannot access another user's original Private Notes, including the person
          described in a note. BlueWire may access or disclose Private Notes when necessary
          to operate the service, provide support, enforce our terms, protect safety or
          security, comply with legal obligations, or respond to valid legal process.
        </p>
      </section>

      <section className="legal-section">
        <h2>Fieldbook</h2>
        <p>
          Fieldbook presents shared, read-only records or snapshots contributed by users.
          Authorized organization members may add separate interactions to an organization
          record. Sharing does not give another user access to the original Private Note.
          Organization Fieldbook and Global Fieldbook are distinct scopes. Organization
          records are available within the applicable organization to users authorized for
          that scope. Global records are shared for access by verified BlueWire users with
          Global Fieldbook access. Users choose whether to share eligible notes or person
          records at either scope. Removing a share affects the shared copy and does not
          give other users access to the original Private Note. Some global snapshots may
          reflect later changes to the source note or person record.
        </p>
        <p>
          Fieldbook searches return only a limited set of active records the user is
          authorized to access in the selected scope. Search results may omit information,
          may be outdated or incomplete, and are investigative leads rather than verified
          facts. Opening a result is subject to the same scope and access checks. Users
          should independently verify information before relying on it.
        </p>
      </section>

      <section className="legal-section">
        <h2>How We Use Information</h2>
        <p>We use your information to:</p>
        <ul>
          <li>Authenticate and verify your account</li>
          <li>Confirm eligibility and agency affiliation</li>
          <li>Connect you with other verified users by agency, rank, or assignment</li>
          <li>Enable messaging, BOLO alerts, Private Notes, and Fieldbook sharing</li>
          <li>Deliver app features such as push notifications and search</li>
          <li>Maintain platform security and detect fraudulent or unauthorized activity</li>
        </ul>
        <p>Your data is never sold, rented, or used for advertising or unrelated commercial purposes.</p>
      </section>

      <section className="legal-section">
        <h2>Sharing of Information</h2>
        <p>
          BlueWire uses service providers to operate the platform and perform identity
          verification. Firebase provides authentication, database, and image-storage
          services. AWS Textract analyzes credential images for text, and AWS Rekognition
          performs a one-time facial comparison between a selfie and the credential
          photograph. AWS Rekognition also processes facial-search images and indexes for
          Private Notes and public booking photographs when those features are enabled.
          If manual review is needed, Resend sends an email alert to the review team
          containing limited contact and verification details.
        </p>
        <p>
          BlueWire does not sell your information or otherwise share it with third parties
          for unrelated purposes. Information you choose to share in Fieldbook is disclosed
          to users with access to the selected Fieldbook scope.
        </p>
      </section>

      <section className="legal-section">
        <h2>Data Retention and Deletion</h2>
        <p>
          Credential and selfie images are normally deleted through scheduled cleanup within
          72 hours after final approval or rejection. An undecided manual review automatically
          expires 30 days after the original submission; its images and temporary verification
          information are then eligible for cleanup. Limited verification records become
          eligible for scheduled deletion beginning 60 days after original submission.
          Extracted credential text has an independent 90-day expiration safeguard. Cleanup
          may take longer because of technical processing delays or failures.
        </p>
        <p>
          These short-lived registration images are separate from longer-lived facial indexes
          associated with Private Notes and public booking photographs. Public booking records
          are retained for up to ten years from first observation, subject to earlier removal
          or suppression as described above. Public-record cleanup processes are implemented;
          actual deletion depends on the applicable process being enabled and completing
          successfully.
        </p>
        <p>Your account data is retained for as long as your account is active.</p>
        <p>
          New Private Notes expire three years after their original note date. Private person
          records expire three years after the most recent interaction on the record. When a
          managed note or person record is within 30 days of expiration, the app may show a
          warning and allow the user to extend the record for three months.
        </p>
        <p>
          When a private note, person record, or account is deleted, BlueWire deletes related
          private note data, attachments, profile photos, facial-search enrollment data,
          search mappings, temporary files, shared read-only copies, and related cached or
          reference data where applicable. BlueWire may keep a minimal destruction log for
          audit purposes. That log does not include the deleted narrative, names, photographs,
          or facial identifiers.
        </p>
        <p>
          If you delete a message, it is removed from all participants and permanently deleted
          from our systems. If you delete your account, personal profile information,
          verification data, and Private Notes are deleted. Certain content, such as messages,
          may remain visible to other users where it has already been distributed within the
          platform.
        </p>
      </section>

      <section className="legal-section">
        <h2>User Rights</h2>
        <p>You may:</p>
        <ul>
          <li>Edit or update your profile information within the app</li>
          <li>Delete your messages, notes, or your entire account at any time</li>
          <li>
            Contact us for assistance with your data at{" "}
            <a href="mailto:thinlineteam@gmail.com">thinlineteam@gmail.com</a>
          </li>
        </ul>
      </section>

      <section className="legal-section">
        <h2>Security</h2>
        <p>
          We implement appropriate technical and organizational safeguards to protect your
          data. All data is encrypted in transit and stored securely. Access is restricted
          to authenticated users, subject to the access scope applicable to each feature.
        </p>
      </section>

      <section className="legal-section">
        <h2>Children's Privacy</h2>
        <p>
          BlueWire is intended only for verified law enforcement and public safety personnel
          who are 21 years of age or older. We do not knowingly collect information from
          children. If we become aware of such data, it will be deleted promptly.
        </p>
      </section>

      <section className="legal-section">
        <h2>Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted at
          this URL, and continued use of BlueWire constitutes acceptance of the updated
          policy.
        </p>
      </section>
    </main>
  );
}
